import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  pageTitleClass,
  pageSubtitleClass,
  cardClass,
  cardTitleClass,
  btnPrimaryClass,
  btnGhostClass,
  formLabelClass,
  formControlClass,
  requiredMarkClass,
  formGridClass,
  alertErrorClass,
  listItemClass,
  listItemTitleClass,
  itemBadgeClass,
  btnIconEditClass,
  btnIconDeleteClass,
  modalOverlayClass,
  modalPanelClass,
  modalHeaderClass,
  modalBodyClass,
  modalFooterClass,
  modalCloseClass,
  spinnerClass,
} from "../dashboardStyles";
import {
  GetServicesAction,
  CreateServiceAction,
  UpdateServiceAction,
  DeleteServiceAction,
  GetCategoriesAction,
  CreateCategoryAction,
  UpdateCategoryAction,
  DeleteCategoryAction,
  GetResourcesAction,
} from "../../../store/action";
import { notify, notifySaveError } from "../../../utils/notify";

const CategoryTeamPicker = ({ resources, selectedIds, onChange, disabled, t }) => (
  <fieldset disabled={disabled} className="min-w-0">
    <legend className={formLabelClass}>{t("Dashboard.Services.categoryTeam")}</legend>
    <p className="mb-2 text-fluid-sm text-ink-500">{t("Dashboard.Services.categoryTeamHint")}</p>
    <div className="flex flex-wrap gap-2">
      {resources.map((resource) => (
        <label key={resource.id} className="cursor-pointer">
          <input type="checkbox" className="peer sr-only" checked={selectedIds.includes(resource.id)}
            onChange={(event) => onChange(event.target.checked
              ? [...selectedIds, resource.id]
              : selectedIds.filter((id) => id !== resource.id))} />
          <span className="inline-flex min-h-10 items-center gap-2 rounded-full border border-ink-200 bg-white px-3 py-1.5 text-sm text-ink-700 transition hover:border-brand-400 peer-checked:border-brand-600 peer-checked:bg-brand-50 peer-checked:text-brand-800 peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-brand-600">
            <i className={`bi ${selectedIds.includes(resource.id) ? "bi-check-circle-fill text-brand-600" : "bi-circle text-ink-400"}`} aria-hidden="true" />
            {resource.name}
          </span>
        </label>
      ))}
    </div>
    {resources.length === 0 && <p className="text-fluid-sm text-ink-500">{t("Dashboard.Services.categoryTeamEmpty")}</p>}
  </fieldset>
);

const UncategorizedServicePicker = ({ services, selectedIds, onChange, disabled, t }) => (
  <fieldset disabled={disabled} className="min-w-0">
    <legend className={formLabelClass}>{t("Dashboard.Services.uncategorizedServicesLabel")}</legend>
    <p className="mb-2 text-fluid-sm text-ink-500">{t("Dashboard.Services.uncategorizedServicesHint")}</p>
    {services.length === 0 ? (
      <p className="rounded-lg bg-ink-50 px-3 py-2 text-sm text-ink-500">{t("Dashboard.Services.uncategorizedServicesEmpty")}</p>
    ) : (
      <div className="max-h-44 space-y-1 overflow-y-auto rounded-lg border border-ink-200 bg-white p-1">
        {services.map((service) => (
          <label key={service.id} className="flex min-h-10 cursor-pointer items-center gap-3 rounded-md px-3 py-2 text-sm text-ink-700 hover:bg-brand-50">
            <input type="checkbox" checked={selectedIds.includes(service.id)}
              onChange={(event) => onChange(event.target.checked
                ? [...selectedIds, service.id]
                : selectedIds.filter((id) => id !== service.id))}
              className="size-4 accent-brand-600" />
            <span className="min-w-0 flex-1 break-words">{service.name}</span>
          </label>
        ))}
      </div>
    )}
  </fieldset>
);

const DashboardServices = () => {
  const { t } = useTranslation();
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [resources, setResources] = useState([]);
  const [resourcesLoading, setResourcesLoading] = useState(true);
  const [resourcesError, setResourcesError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    duration: "",
    durationUnit: "Minutes",
    categoryId: "",
  });
  const [saving, setSaving] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState("all");
  const [serviceSearch, setServiceSearch] = useState("");

  // Category management modal state
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [newCategoryResourceIds, setNewCategoryResourceIds] = useState([]);
  const [newCategoryServiceIds, setNewCategoryServiceIds] = useState([]);
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [editingCategoryName, setEditingCategoryName] = useState("");
  const [editingCategoryResourceIds, setEditingCategoryResourceIds] = useState([]);
  const [editingCategoryServiceIds, setEditingCategoryServiceIds] = useState([]);
  const [categorySaving, setCategorySaving] = useState(false);

  useEffect(() => {
    fetchServices();
    fetchCategories();
    fetchResources();
  }, []);

  const fetchServices = async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);
      const data = await GetServicesAction();
      setServices(data);
      setError(null);
    } catch (err) {
      console.error("Error fetching services:", err);
      setError(t("Dashboard.Services.loadError"));
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const data = await GetCategoriesAction();
      setCategories(data);
    } catch (err) {
      // Categories are optional; a load failure should not block the page.
      console.error("Error fetching categories:", err);
      setCategories([]);
    }
  };

  const fetchResources = async () => {
    setResourcesLoading(true);
    setResourcesError(false);
    try {
      setResources(await GetResourcesAction());
    } catch {
      setResourcesError(true);
    } finally {
      setResourcesLoading(false);
    }
  };

  const teamUnavailable = resourcesLoading || resourcesError;
  const teamSummary = (ids = []) => ids.length === 0
    ? t("Dashboard.Services.categoryTeamAll")
    : resources.filter((resource) => ids.includes(resource.id)).map((resource) => resource.name).join(", ");

  const categoryCount = (id) => services.filter((service) => service.categoryId === id).length;
  const uncategorizedCount = services.filter((service) => service.categoryId == null).length;
  const uncategorizedServices = services.filter((service) => service.categoryId == null);
  const selectedCategory = categories.find((category) => String(category.id) === selectedCategoryId);
  const searchTerm = serviceSearch.trim().toLocaleLowerCase();
  const visibleServices = services.filter((service) => {
    const matchesCategory = selectedCategoryId === "all"
      || (selectedCategoryId === "uncategorized" && service.categoryId == null)
      || String(service.categoryId) === selectedCategoryId;
    const matchesSearch = !searchTerm || [service.name, service.description, service.categoryName]
      .some((value) => value?.toLocaleLowerCase().includes(searchTerm));
    return matchesCategory && matchesSearch;
  });

  const openCategories = () => {
    setEditingCategoryId(null);
    setEditingCategoryName("");
    setEditingCategoryResourceIds([]);
    setEditingCategoryServiceIds([]);
    setShowCategoryModal(true);
  };

  const handleAddCategory = async (e) => {
    e.preventDefault();
    if (!newCategoryName.trim() || teamUnavailable) return;
    try {
      setCategorySaving(true);
      await CreateCategoryAction({ name: newCategoryName.trim(), resourceIds: newCategoryResourceIds, serviceIds: newCategoryServiceIds });
      setNewCategoryName("");
      setNewCategoryResourceIds([]);
      setNewCategoryServiceIds([]);
      notify.success(t("Dashboard.Services.categoryCreated"));
      await Promise.all([fetchCategories(), fetchServices(false)]);
    } catch (err) {
      notifySaveError(err, t("Dashboard.Services.categorySaveError"));
    } finally {
      setCategorySaving(false);
    }
  };

  const handleSaveCategory = async (id) => {
    if (!editingCategoryName.trim() || teamUnavailable) return;
    try {
      setCategorySaving(true);
      await UpdateCategoryAction(id, { name: editingCategoryName.trim(), resourceIds: editingCategoryResourceIds, serviceIds: editingCategoryServiceIds });
      setEditingCategoryId(null);
      setEditingCategoryName("");
      setEditingCategoryServiceIds([]);
      notify.success(t("Dashboard.Services.categoryUpdated"));
      await Promise.all([fetchCategories(), fetchServices(false)]);
    } catch (err) {
      notifySaveError(err, t("Dashboard.Services.categorySaveError"));
    } finally {
      setCategorySaving(false);
    }
  };

  const handleDeleteCategory = async (id) => {
    if (!window.confirm(t("Dashboard.Services.categoryDeleteConfirm"))) return;
    try {
      setCategorySaving(true);
      await DeleteCategoryAction(id);
      if (selectedCategoryId === String(id)) setSelectedCategoryId("all");
      notify.success(t("Dashboard.Services.categoryDeleted"));
      // Services that sat in this category are now uncategorized; refresh both.
      await Promise.all([fetchCategories(), fetchServices(false)]);
    } catch (err) {
      notifySaveError(err, t("Dashboard.Services.categoryDeleteError"));
    } finally {
      setCategorySaving(false);
    }
  };

  const handleOpenModal = (service = null) => {
    if (service) {
      setEditingService(service);
      setFormData({
        name: service.name || "",
        description: service.description || "",
        duration: service.duration || "",
        durationUnit: service.durationUnit || "Minutes",
        categoryId: service.categoryId != null ? String(service.categoryId) : "",
      });
    } else {
      setEditingService(null);
      setFormData({
        name: "",
        description: "",
        duration: "",
        durationUnit: "Minutes",
        categoryId: selectedCategoryId !== "all" && selectedCategoryId !== "uncategorized"
          ? selectedCategoryId : "",
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingService(null);
    setFormData({
      name: "",
      description: "",
      duration: "",
      durationUnit: "Minutes",
      categoryId: "",
    });
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const serviceData = {
        name: formData.name,
        description: formData.description || null,
        duration: parseInt(formData.duration),
        durationUnit: formData.durationUnit,
        categoryId: formData.categoryId ? parseInt(formData.categoryId) : null,
        organizationId: 0, // Backend will set this from user context
      };

      if (editingService) {
        await UpdateServiceAction(editingService.id, serviceData);
      } else {
        await CreateServiceAction(serviceData);
      }

      await fetchServices();
      handleCloseModal();
      notify.success(
        t(editingService ? "Dashboard.Services.updated" : "Dashboard.Services.created"),
      );
    } catch (err) {
      // The modal is still open over the page, so this cannot be a page alert.
      console.error("Error saving service:", err);
      notifySaveError(err, t("Dashboard.Services.saveError"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t("Dashboard.Services.deleteConfirm"))) {
      return;
    }

    try {
      await DeleteServiceAction(id);
      notify.success(t("Dashboard.Services.deleted"));
      await fetchServices();
    } catch (err) {
      console.error("Error deleting service:", err);
      notifySaveError(err, t("Dashboard.Services.deleteError"));
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div
          role="status"
          aria-label={t("Dashboard.Services.loading")}
          className={spinnerClass}
        />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className={pageTitleClass}>{t("Dashboard.Services.title")}</h1>
          <p className={pageSubtitleClass}>{t("Dashboard.Services.subtitle")}</p>
        </div>
        <button className={btnPrimaryClass} onClick={() => handleOpenModal()}>
          <i className="bi bi-plus-lg" aria-hidden="true" />
          {t("Dashboard.Services.addService")}
        </button>
      </div>

      {error && (
        <div className={alertErrorClass} role="alert">
          <i className="bi bi-exclamation-circle mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <div className="grid items-start gap-5 lg:grid-cols-[15rem_minmax(0,1fr)]">
        <aside className={`${cardClass} p-3`} aria-label={t("Dashboard.Services.categoriesTitle")}>
          <div className="flex items-center justify-between gap-2 px-2 pb-3 pt-1">
            <h2 className="m-0 text-xs font-bold uppercase tracking-wider text-ink-500">
              {t("Dashboard.Services.categoriesTitle")}
            </h2>
            <button
              type="button"
              className="flex size-9 items-center justify-center rounded-lg text-brand-700 transition hover:bg-brand-50 focus-visible:outline-2 focus-visible:outline-brand-600"
              onClick={openCategories}
              aria-label={t("Dashboard.Services.manageCategories")}
              title={t("Dashboard.Services.manageCategories")}
            >
              <i className="bi bi-gear" aria-hidden="true" />
            </button>
          </div>
          <nav className="flex gap-1.5 overflow-x-auto pb-1 lg:flex-col" aria-label={t("Dashboard.Services.categoryFilterLabel")}>
            <button type="button" aria-pressed={selectedCategoryId === "all"} onClick={() => setSelectedCategoryId("all")}
              className={`flex min-h-11 shrink-0 items-center justify-between gap-3 rounded-lg px-3 text-left text-sm font-medium transition focus-visible:outline-2 focus-visible:outline-brand-600 lg:w-full ${selectedCategoryId === "all" ? "bg-brand-50 text-brand-800" : "text-ink-600 hover:bg-ink-50 hover:text-ink-900"}`}>
              <span className="flex items-center gap-2"><i className="bi bi-grid" aria-hidden="true" />{t("Dashboard.Services.allServices")}</span>
              <span className="text-xs tabular-nums">{services.length}</span>
            </button>
            {categories.map((category) => (
              <button key={category.id} type="button" aria-pressed={selectedCategoryId === String(category.id)}
                onClick={() => setSelectedCategoryId(String(category.id))}
                className={`flex min-h-11 shrink-0 items-center justify-between gap-3 rounded-lg px-3 text-left text-sm font-medium transition focus-visible:outline-2 focus-visible:outline-brand-600 lg:w-full ${selectedCategoryId === String(category.id) ? "bg-brand-50 text-brand-800" : "text-ink-600 hover:bg-ink-50 hover:text-ink-900"}`}>
                <span className="flex min-w-0 items-center gap-2"><i className="bi bi-folder2 shrink-0" aria-hidden="true" /><span className="line-clamp-2">{category.name}</span></span>
                <span className="text-xs tabular-nums">{categoryCount(category.id)}</span>
              </button>
            ))}
            {uncategorizedCount > 0 && (
              <button type="button" aria-pressed={selectedCategoryId === "uncategorized"} onClick={() => setSelectedCategoryId("uncategorized")}
                className={`flex min-h-11 shrink-0 items-center justify-between gap-3 rounded-lg px-3 text-left text-sm font-medium transition focus-visible:outline-2 focus-visible:outline-brand-600 lg:w-full ${selectedCategoryId === "uncategorized" ? "bg-brand-50 text-brand-800" : "text-ink-600 hover:bg-ink-50 hover:text-ink-900"}`}>
                <span className="flex items-center gap-2"><i className="bi bi-folder-x" aria-hidden="true" />{t("Dashboard.Services.noCategory")}</span>
                <span className="text-xs tabular-nums">{uncategorizedCount}</span>
              </button>
            )}
          </nav>
          <button type="button" className="mt-3 flex w-full items-center gap-2 rounded-lg border border-dashed border-brand-300 px-3 py-2.5 text-left text-sm font-semibold text-brand-700 transition hover:bg-brand-50 focus-visible:outline-2 focus-visible:outline-brand-600"
            onClick={openCategories}>
            <i className="bi bi-plus-lg" aria-hidden="true" />{t("Dashboard.Services.addCategoryButton")}
          </button>
        </aside>

        <section className={cardClass} aria-label={t("Dashboard.Services.allServices")}>
          <div className="flex flex-col gap-4 border-b border-ink-200 p-5 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className={cardTitleClass}>{selectedCategory?.name || (selectedCategoryId === "uncategorized" ? t("Dashboard.Services.noCategory") : t("Dashboard.Services.allServices"))}</h2>
              <p className="mt-1 text-sm text-ink-500">{t("Dashboard.Services.serviceCount", { count: visibleServices.length })}</p>
            </div>
            <div className="relative w-full sm:max-w-64">
              <i className="bi bi-search pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" aria-hidden="true" />
              <input type="search" value={serviceSearch} onChange={(event) => setServiceSearch(event.target.value)}
                aria-label={t("Dashboard.Services.searchServices")}
                placeholder={t("Dashboard.Services.searchServices")}
                className="w-full rounded-lg border border-ink-200 bg-white py-2.5 pl-10 pr-3 text-sm text-ink-900 placeholder:text-ink-400 focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-600/20" />
            </div>
          </div>
          {visibleServices.length === 0 ? (
            <div className="flex flex-col items-center px-5 py-16 text-center">
              <div className="mb-4 flex size-14 items-center justify-center rounded-2xl bg-brand-50 text-2xl text-brand-600"><i className="bi bi-collection" aria-hidden="true" /></div>
              <p className="font-semibold text-ink-900">{services.length === 0 ? t("Dashboard.Services.emptyState") : t("Dashboard.Services.noMatchingServices")}</p>
              {services.length === 0 && <button type="button" className={`${btnPrimaryClass} mt-5`} onClick={() => handleOpenModal()}>{t("Dashboard.Services.addService")}</button>}
              {services.length > 0 && <button type="button" className={`${btnGhostClass} mt-3`} onClick={() => { setSelectedCategoryId("all"); setServiceSearch(""); }}>{t("Dashboard.Services.clearFilters")}</button>}
            </div>
          ) : (
            <div className="divide-y divide-ink-100">
              {visibleServices.map((service) => (
                <article key={service.id} className="flex flex-col gap-3 p-5 transition hover:bg-ink-50/70 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex min-w-0 items-start gap-3">
                    <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-brand-50 text-brand-700"><i className="bi bi-calendar2-check" aria-hidden="true" /></div>
                    <div className="min-w-0">
                      <h3 className={listItemTitleClass}>{service.name}</h3>
                      {service.description && <p className="mt-0.5 line-clamp-2 text-sm text-ink-500">{service.description}</p>}
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        <span className={itemBadgeClass}><i className="bi bi-clock" aria-hidden="true" />{t("Dashboard.Services.durationBadge", { duration: service.duration, unit: service.durationUnit })}</span>
                        {service.categoryName && <span className={itemBadgeClass}><i className="bi bi-folder2" aria-hidden="true" />{service.categoryName}</span>}
                        {!teamUnavailable && <span className="text-xs text-ink-500"><i className="bi bi-people mr-1" aria-hidden="true" />{teamSummary(service.resourceIds)}</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex shrink-0 justify-end gap-1.5">
                    <button className={btnIconEditClass} onClick={() => handleOpenModal(service)}
                      aria-label={t("Dashboard.Services.editAriaLabel", { name: service.name })}>
                      <i className="bi bi-pencil" aria-hidden="true" />
                    </button>
                    <button className={btnIconDeleteClass} onClick={() => handleDelete(service.id)}
                      aria-label={t("Dashboard.Services.deleteAriaLabel", { name: service.name })}>
                      <i className="bi bi-trash" aria-hidden="true" />
                    </button>
                  </div>
                </article>
              ))}
            </div>
          )}
          <div className="border-t border-ink-100 bg-ink-50 px-5 py-3 text-xs text-ink-500">
            {t("Dashboard.Services.catalogHint")}
          </div>
        </section>
      </div>

      {/* Service Modal */}
      {showModal && (
        <div
          className={modalOverlayClass}
          onClick={handleCloseModal}
          role="dialog"
          aria-modal="true"
          aria-labelledby="service-modal-title"
        >
          <div className={modalPanelClass} onClick={(e) => e.stopPropagation()}>
            <div className={modalHeaderClass}>
              <h3 id="service-modal-title" className={cardTitleClass}>
                {editingService
                  ? t("Dashboard.Services.editServiceTitle")
                  : t("Dashboard.Services.addNewServiceTitle")}
              </h3>
              <button
                className={modalCloseClass}
                onClick={handleCloseModal}
                aria-label={t("Dashboard.Services.closeAriaLabel")}
              >
                <i className="bi bi-x text-xl" />
              </button>
            </div>
            <form
              onSubmit={handleSubmit}
              className="flex min-h-0 flex-1 flex-col"
            >
              <div className={`${modalBodyClass} flex flex-col gap-4`}>
                <div>
                  <label htmlFor="name" className={formLabelClass}>
                    {t("Dashboard.Services.serviceNameLabel")}{" "}
                    <span className={requiredMarkClass}>*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleFormChange}
                    required
                    className={formControlClass}
                    placeholder={t("Dashboard.Services.serviceNamePlaceholder")}
                  />
                </div>

                <div>
                  <label htmlFor="description" className={formLabelClass}>
                    {t("Dashboard.Services.descriptionLabel")}
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleFormChange}
                    className={formControlClass}
                    rows="3"
                    placeholder={t(
                      "Dashboard.Services.descriptionPlaceholder"
                    )}
                  />
                </div>

                {categories.length > 0 && (
                  <div>
                    <label htmlFor="categoryId" className={formLabelClass}>
                      {t("Dashboard.Services.categoryLabel")}
                    </label>
                    <select
                      id="categoryId"
                      name="categoryId"
                      value={formData.categoryId}
                      onChange={handleFormChange}
                      className={formControlClass}
                    >
                      <option value="">
                        {t("Dashboard.Services.noCategory")}
                      </option>
                      {categories.map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                    <p className="mt-2 text-fluid-sm text-ink-500">{t("Dashboard.Services.serviceTeamHint")}</p>
                  </div>
                )}

                <div className={formGridClass}>
                  <div>
                    <label htmlFor="duration" className={formLabelClass}>
                      {t("Dashboard.Services.durationLabel")}{" "}
                      <span className={requiredMarkClass}>*</span>
                    </label>
                    <input
                      type="number"
                      id="duration"
                      name="duration"
                      value={formData.duration}
                      onChange={handleFormChange}
                      required
                      min="1"
                      className={formControlClass}
                      placeholder={t("Dashboard.Services.durationLabel")}
                    />
                  </div>

                  <div>
                    <label htmlFor="durationUnit" className={formLabelClass}>
                      {t("Dashboard.Services.unitLabel")}
                    </label>
                    <select
                      id="durationUnit"
                      name="durationUnit"
                      value={formData.durationUnit}
                      onChange={handleFormChange}
                      className={formControlClass}
                    >
                      <option value="Minutes">
                        {t("Dashboard.Services.unitMinutes")}
                      </option>
                      <option value="Hours">
                        {t("Dashboard.Services.unitHours")}
                      </option>
                    </select>
                  </div>
                </div>
              </div>

              <div className={modalFooterClass}>
                <button
                  type="button"
                  className={btnGhostClass}
                  onClick={handleCloseModal}
                  disabled={saving}
                >
                  {t("Dashboard.Services.cancel")}
                </button>
                <button
                  type="submit"
                  className={btnPrimaryClass}
                  disabled={saving}
                >
                  {saving
                    ? t("Dashboard.Services.saving")
                    : editingService
                      ? t("Dashboard.Services.updateService")
                      : t("Dashboard.Services.addService")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Category Management Modal */}
      {showCategoryModal && (
        <div
          className={modalOverlayClass}
          onClick={() => setShowCategoryModal(false)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="category-modal-title"
        >
          <div className={modalPanelClass} onClick={(e) => e.stopPropagation()}>
            <div className={modalHeaderClass}>
              <h3 id="category-modal-title" className={cardTitleClass}>
                {t("Dashboard.Services.categoriesTitle")}
              </h3>
              <button
                className={modalCloseClass}
                onClick={() => setShowCategoryModal(false)}
                aria-label={t("Dashboard.Services.closeAriaLabel")}
              >
                <i className="bi bi-x text-xl" />
              </button>
            </div>
            <div className={`${modalBodyClass} flex flex-col gap-5`}>
              <p className="text-sm leading-relaxed text-ink-500">
                {t("Dashboard.Services.categoriesHint")}
              </p>

              {resourcesError && (
                <div className={alertErrorClass} role="alert">
                  <span>{t("Dashboard.Services.categoryTeamLoadError")}</span>
                  <button type="button" className={btnGhostClass} onClick={fetchResources}>{t("Dashboard.Services.retryTeam")}</button>
                </div>
              )}

              <form onSubmit={handleAddCategory} className="flex flex-col gap-4 rounded-xl border border-brand-200 bg-brand-50/50 p-4">
                <div className="flex items-center gap-2 text-sm font-semibold text-ink-900">
                  <i className="bi bi-plus-circle text-brand-700" aria-hidden="true" />
                  {t("Dashboard.Services.addCategoryButton")}
                </div>
                <input
                  type="text"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  className={formControlClass}
                  placeholder={t("Dashboard.Services.categoryNamePlaceholder")}
                  aria-label={t("Dashboard.Services.categoryNamePlaceholder")}
                  maxLength={100}
                />
                <CategoryTeamPicker resources={resources} selectedIds={newCategoryResourceIds} onChange={setNewCategoryResourceIds} disabled={categorySaving || teamUnavailable} t={t} />
                <UncategorizedServicePicker services={uncategorizedServices} selectedIds={newCategoryServiceIds} onChange={setNewCategoryServiceIds} disabled={categorySaving} t={t} />
                <button
                  type="submit"
                  className={btnPrimaryClass}
                  disabled={categorySaving || teamUnavailable || !newCategoryName.trim()}
                >
                  <i className="bi bi-plus-circle" />
                  {t("Dashboard.Services.addCategory")}
                </button>
              </form>

              <div className="flex items-center justify-between gap-3 border-b border-ink-200 pb-2">
                <h4 className="m-0 text-sm font-semibold text-ink-900">{t("Dashboard.Services.existingCategories")}</h4>
                <span className="rounded-full bg-ink-100 px-2.5 py-1 text-xs font-semibold tabular-nums text-ink-600">{categories.length}</span>
              </div>
              {categories.length === 0 ? (
                <p className="py-4 text-center text-fluid-sm text-ink-500">
                  {t("Dashboard.Services.categoriesEmpty")}
                </p>
              ) : (
                <div className="flex flex-col gap-2">
                  {categories.map((category) => (
                    <div key={category.id} className={`${listItemClass} flex-col sm:flex-row`}>
                      {editingCategoryId === category.id ? (
                        <div className="flex min-w-0 flex-1 flex-col gap-3">
                          <input
                            type="text"
                            value={editingCategoryName}
                            aria-label={t("Dashboard.Services.categoryNamePlaceholder")}
                            onChange={(e) =>
                              setEditingCategoryName(e.target.value)
                            }
                            className={formControlClass}
                            maxLength={100}
                            autoFocus
                          />
                          <CategoryTeamPicker resources={resources} selectedIds={editingCategoryResourceIds} onChange={setEditingCategoryResourceIds} disabled={categorySaving || teamUnavailable} t={t} />
                          <UncategorizedServicePicker services={uncategorizedServices} selectedIds={editingCategoryServiceIds} onChange={setEditingCategoryServiceIds} disabled={categorySaving} t={t} />
                          <div className="flex gap-2">
                            <button
                              className={btnPrimaryClass}
                              onClick={() => handleSaveCategory(category.id)}
                              disabled={categorySaving || teamUnavailable || !editingCategoryName.trim()}
                            >
                              {t("Dashboard.Services.saveCategory")}
                            </button>
                            <button
                              className={btnGhostClass}
                              onClick={() => {
                                setEditingCategoryId(null);
                                setEditingCategoryName("");
                                setEditingCategoryServiceIds([]);
                              }}
                            >
                              {t("Dashboard.Services.cancel")}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="flex min-w-0 flex-1 items-start gap-3">
                            <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-700"><i className="bi bi-folder2" aria-hidden="true" /></div>
                            <div className="min-w-0">
                              <span className={listItemTitleClass}>{category.name}</span>
                              <p className="mt-0.5 text-xs text-ink-500">{t("Dashboard.Services.categoryServiceCount", { count: categoryCount(category.id) })}</p>
                              {!teamUnavailable && <p className="mt-1 text-xs text-ink-500"><i className="bi bi-people mr-1" aria-hidden="true" />{teamSummary(category.resourceIds)}</p>}
                            </div>
                          </div>
                          <div className="flex shrink-0 flex-wrap justify-end gap-1.5">
                            <button
                              className={btnGhostClass}
                              onClick={() => {
                                setEditingCategoryId(category.id);
                                setEditingCategoryName(category.name);
                                setEditingCategoryResourceIds(category.resourceIds ?? []);
                                setEditingCategoryServiceIds([]);
                              }}
                              aria-label={t(
                                "Dashboard.Services.editCategoryAriaLabel",
                                { name: category.name }
                              )}
                            >
                              <i className="bi bi-pencil" aria-hidden="true" /> {t("Dashboard.Services.assignCategoryTeam")}
                            </button>
                            <button
                              className={btnIconDeleteClass}
                              onClick={() => handleDeleteCategory(category.id)}
                              aria-label={t(
                                "Dashboard.Services.deleteCategoryAriaLabel",
                                { name: category.name }
                              )}
                            >
                              <i className="bi bi-trash" />
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardServices;
